// Shared tracking logic: registers a single analytics middleware that stamps
// globally-common attributes (currently `Source`) onto every tracked event.

import { addMiddleware } from '@ixigo/analytics';
import { createTracker } from '@/lib/analytics/create-tracker';

// Derived from `addMiddleware` to stay in sync with the package.
// TODO(@ixigo/analytics): re-export `Middleware` from the entrypoint, then
// import it directly (it's declared in the package but not exposed publicly).
type Middleware = Parameters<typeof addMiddleware>[0];

/**
 * Shared base tracker for funnels that pass a common track function down to
 * child components (see `TrackerProvider`). `Source` is added globally by
 * `sourceMiddleware`, so this no longer needs its own common-attributes context.
 */
export const globalTracker = createTracker({
	trackMap: {},
});

export function getCommonAttributes() {
	const source = getSourceFromLocation();
	const commonAttributes: Record<string, string> = {};
	if (source) {
		commonAttributes.Source = source;
	}
	return commonAttributes;
}

export function getSourceFromLocation() {
	if (typeof window === 'undefined') return;
	return new URLSearchParams(window.location.search).get('source') || undefined;
}

/**
 * Stamps common attributes (currently `Source` from `?source=`) onto EVERY
 * tracked event at the analytics boundary, so no event can ship without it.
 *
 * Precedence: an event's own `Source` wins (`ctx.data` spread last); otherwise
 * the live URL source is used (read per-event, not snapshotted, so it reflects
 * source changes across navigation).
 */
export const sourceMiddleware: Middleware = (ctx, next) => {
	next({
		...ctx,
		data: {
			...getCommonAttributes(),
			...ctx.data,
		},
	});
};

export function initGlobalAppTracker() {
	// Register once at the analytics boundary so every event gets `Source`.
	addMiddleware(sourceMiddleware);
	const defaultAttributes = getCommonAttributes();
	globalTracker.setContext(() => ({
		getCommonAttributes: () => ({
			...defaultAttributes,
			...getCommonAttributes(),
		}),
	}));
}
